/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import AddButton from 'components/AddButton.jsx';
import Avatar from 'components/Avatar.jsx';
import Button from 'components/Button.jsx';
import ChatStore from 'chat/stores/ChatStore';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import GroupStore from 'groups/stores/GroupStore';
import KeyConstants from 'constants/KeyConstants';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import RoleBasedMessagingResultItem from 'chat/components/RoleBasedMessagingResultItem.jsx';
import ScrollableArea from 'components/ScrollableArea.jsx';
import Text from 'components/Text.jsx';
import TokenizerTypeahead from 'components/TokenizerTypeahead.jsx';
import RecipientToken from 'chat/components/UniversalComposerRecipientToken.jsx';
import { EVERYONE, subgroupName } from 'utils/roleHelpers';
import { SEARCH_NETWORK_LIMIT_MS } from 'chat/constants/ChatConstants';
import {
  changeQuery,
  loadGroups,
  loadIndividuals,
  loadPotentialChatMembers,
  select,
  selectInitialTargets,
  selectAllGroups,
  changeResultSelection,
  changeHighlightIndex,
  flush,
  RECIPIENT_TYPES,
} from 'reducers/roleBasedMessagingSearch';
import type { Recipient } from 'reducers/roleBasedMessagingSearch';
import type { Target } from 'entities/Message';

import './styles/RoleBasedMessagingRecipientsInput.scss';
import './styles/RoleBasedMessagingResultItem.scss';

export class RoleBasedMessagingRecipientsInput extends React.Component {
  props: {
    autoFocus?: bool,
    hideResults?: bool,
    highlightIndex: number,
    initialTargets?: Target[],
    onActionButtonClick: () => void,
    onChangeQuery: (query: string) => void,
    onChangeResultSelection: (selectedIndex: number) => void,
    onEnter: () => void,
    onFlush: () => void,
    onHighlightIndexChange: (index: number) => void,
    onInputFocus: () => void,
    onLoadGroups: (groups: Group[]) => void,
    onLoadIndividuals: (members: PotentialChatMember[]) => void,
    onLoadPotentialChatMembers: (query: string) => void,
    onSelect: (recipients: Recipient[]) => void,
    onSelectAllGroups: () => void,
    onSelectInitialTargets: (targets: Target[]) => void,
    resultSelectionIndex: number,
    results: Array<Recipient[]>,
    query: string,
    selection: Recipient[],
    showIndividuals: boolean,
    className?: string,
  };

  static defaultProps = {
    autoFocus: false,
    hideResults: true,
    showIndividuals: false,
    query: '',
  };

  state: {
    focus: bool,
  };

  state = {
    focus: false,
  };

  componentWillMount() {
    const {
      onLoadGroups,
      onLoadIndividuals,
      initialTargets,
      onSelectInitialTargets,
      showIndividuals,
    } = this.props;
    // $FlowFixMe https://github.com/remind101/r101-frontend/pull/4065
    onLoadGroups(GroupStore.getGroups());
    if (showIndividuals) {
      onLoadIndividuals(new PotentialChatMembers(ChatStore.getEligibleMemberModels()));
    }
    if (initialTargets && initialTargets.length) {
      onSelectInitialTargets(initialTargets);
    }
  }

  componentWillUnmount() {
    this.props.onFlush();
  }

  debouncedLoadPotentialChatMembers = debounce(function(query) {
    this.props.onLoadPotentialChatMembers(query);
  }, SEARCH_NETWORK_LIMIT_MS);

  handleActionButtonClick = () => {
    if (this.state.focus) {
      this.setState({ focus: false });
    }
    this.props.onActionButtonClick();
  }

  handleInputChange = (query: string) => {
    // Calls onChangeQuery first so that the current page of individuals
    // can be searched while additional results from the backend are still
    // being fetched
    this.props.onChangeQuery(query);
    if (this.props.showIndividuals) {
      this.debouncedLoadPotentialChatMembers(query);
    }
  }

  handleInputFocus = () => {
    if (!this.state.focus) {
      this.setState({ focus: true });
    }
    this.props.onInputFocus();
  }

  // typecheck: ignore statement
  handleInputKeyDown = (e: SyntheticKeyboardEvent) => {
    const { results, highlightIndex, resultSelectionIndex, query } = this.props;
    const { keyCode } = e;
    switch (keyCode) {
      case KeyConstants.DOWN: {
        const highlightedResults: ?Recipient[] = results[highlightIndex];
        if (highlightedResults && query) {
          if (resultSelectionIndex === highlightedResults.length - 1) {
            // If we are at the end, set selection to 0 and allow typeahead to handle the keypress
            this.props.onChangeResultSelection(0);
          } else {
            this.props.onChangeResultSelection(this.props.resultSelectionIndex + 1);
            e.stopPropagation();
          }
        }
        break;
      }
      case KeyConstants.UP: {
        if (query) {
          if (resultSelectionIndex === 0) {
            const previousResults = results[highlightIndex - 1];
            if (previousResults) {
              this.props.onChangeResultSelection(previousResults.length - 1);
            }
          } else {
            this.props.onChangeResultSelection(this.props.resultSelectionIndex - 1);
            e.stopPropagation();
          }
        }
        break;
      }
      default:
        break;
    }
  }

  renderActionButton = () => {
    if (this.state.focus) {
      return (
        <Button
          disabled={!this.props.selection.length}
          className="RoleBasedMessagingRecipientsInput-control-row-action-nextButton"
          data-test="role-based-messaging-recipients-input-next"
          onClick={this.handleActionButtonClick}
          eventElementName="RoleBasedMessagingRecipientsInput-control-row-action-nextButton">
          Next
        </Button>
      );
    } else {
      return (
        <Button
          color="brand"
          level="text"
          className="RoleBasedMessagingRecipientsInput-control-row-action-addButton"
          onClick={this.handleInputFocus}
          eventElementName="RoleBasedMessagingRecipientsInput-control-row-action-addButton">
          <Glyph name="plus" solo />
        </Button>
      );
    }
  }

  renderResultsList = (items: mixed) => {
    return (
      // $FlowIssue
      <ScrollableArea className="RoleBasedMessagingRecipientsInput-list">
        <ul>
          {items}
        </ul>
      </ScrollableArea>
    );
  }

  renderResultItem = (recipients: Recipient[], highlighted: bool, query: string, onSelect: () => void) => {
    const selectedRecipients = this.props.selection.filter((s: Recipient) => {
      const sId = s.type === RECIPIENT_TYPES.GROUP_RECIPIENT ? s.group.id : s.id;
      const r = recipients[0];
      const rId = r.type === RECIPIENT_TYPES.GROUP_RECIPIENT ? r.group.id : r.id;

      return sId === rId;
    });
    return (
      <RoleBasedMessagingResultItem
        recipients={recipients}
        highlighted={highlighted}
        query={query}
        onSelect={onSelect}
        selectedRecipients={selectedRecipients}
        resultSelectionIndex={this.props.resultSelectionIndex}
      />
    );
  }

  renderSelectAllItem = (highlighted: bool, onSelect: () => void) => {
    return (
      <li
        data-test-selector="send-to-all-classes"
        onClick={onSelect}
        className={cx('RoleBasedMessagingResultItem', {
          'RoleBasedMessagingResultItem-highlighted': highlighted,
        })}>
        <Text
          className="RoleBasedMessagingResultItem-select-all"
          color="pitch"
          size="xsmall"
          weight="semibold">
          <AddButton size="large">
            Add all the classes you own
          </AddButton>
        </Text>
      </li>
    );
  }

  // $FlowFixMe destructuring
  renderGroupTokenAvatar = ({ group }) => {
    return <GroupAvatar group={group} size="xsmall" />;
  }

  // $FlowFixMe destructuring
  renderGroupTokenContent = ({ group, role }) => {
    return (
      <div>
        <span className="RoleBasedMessagingRecipientsInput-token-name">
          <GroupName group={group} />
        </span>
        <Glyph
          name="chevron-right"
          size="xxsmall"
          className="RoleBasedMessagingRecipientsInput-token-carat"
        />
        <span className="RoleBasedMessagingRecipientsInput-token-role">
          {subgroupName[role.role || EVERYONE]}
        </span>
      </div>
    );
  }

  // $FlowFixMe destructuring
  renderIndividualTokenAvatar = ({ member }) => {
    return (
      <Avatar
        colorHex={member.getUser().getColor()}
        text={member.getUser().getInitials()}
        size="small"
      />
    );
  }

  // $FlowFixMe destructuring
  renderIndividualTokenContent = ({ member }) => {
    return (
      <div>
        <span className="RoleBasedMessagingRecipientsInput-token-name">
          {member.getName()}
        </span>
      </div>
    );
  }

  // $FlowIssue adding the annotation destroys the prop types on this component 💩💩💩
  renderToken = (recipient) => {
    const avatar = recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT ?
      this.renderGroupTokenAvatar(recipient) :
      this.renderIndividualTokenAvatar(recipient);
    const content = recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT ?
      this.renderGroupTokenContent(recipient) :
      this.renderIndividualTokenContent(recipient);

    return (
      <RecipientToken>
        {avatar}
        <span className="RoleBasedMessagingRecipientsInput-token-container">
          {content}
        </span>
      </RecipientToken>
    );
  }

  renderControlRow = (tokenizer: mixed, actionButton: mixed) => {
    return (
      <div className="RoleBasedMessagingRecipientsInput-control-row">
        {tokenizer}
        <div className="RoleBasedMessagingRecipientsInput-control-row-action">
          {actionButton}
        </div>
      </div>
    );
  }

  noResultsRenderer = () => {
    return (
      <div className="RoleBasedMessagingRecipientsInput-noResult">
        No results for “{this.props.query}”
      </div>
    );
  }

  render() {
    let fullResults;
    if (this.props.query) {
      fullResults = this.props.results;
    } else {
      fullResults = this.props.results.length ?
        ['_all', ...this.props.results] :
        this.props.results;
    }

    return (
      <div className="RoleBasedMessagingRecipientsInput">
        <TokenizerTypeahead
          inline
          className={this.props.className}
          autoFocus={this.props.autoFocus}
          placeholder="Select one or more classes"
          prompt="To:"
          tokenizerClassName="RoleBasedMessagingRecipientsInput-tokenizer"
          controlRowClassName="RoleBasedMessagingRecipientsInput-control-row"
          query={this.props.query}
          results={fullResults}
          selection={this.props.selection}
          onDeselect={(recipient: Recipient) => this.props.onSelect([recipient])}
          onSelect={this.props.onSelect}
          onSelectAll={this.props.onSelectAllGroups}
          onInputFocus={this.handleInputFocus}
          onInputChange={this.handleInputChange}
          onInputKeyDown={this.handleInputKeyDown}
          noResultsRenderer={this.noResultsRenderer}
          onEnter={this.props.onEnter}
          itemRenderer={(...args) => this.renderResultItem(...args)}
          listRenderer={this.renderResultsList}
          selectAllItemRenderer={this.renderSelectAllItem}
          tokenRenderer={this.renderToken}
          controlRowRenderer={this.renderControlRow}
          actionButton={this.renderActionButton()}
          hideResults={this.props.hideResults}
          onHighlightIndexChange={this.props.onHighlightIndexChange}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: Object) => {
  return {
    results: state.roleBasedMessagingSearch.results,
    resultSelectionIndex: state.roleBasedMessagingSearch.resultSelectionIndex,
    query: state.roleBasedMessagingSearch.query,
    selection: state.roleBasedMessagingSearch.selection,
    highlightIndex: state.roleBasedMessagingSearch.highlightIndex,
  };
};

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators({
    onChangeQuery: changeQuery,
    onLoadGroups: loadGroups,
    onLoadIndividuals: loadIndividuals,
    onLoadPotentialChatMembers: loadPotentialChatMembers,
    onSelect: select,
    onSelectInitialTargets: selectInitialTargets,
    onSelectAllGroups: selectAllGroups,
    onChangeResultSelection: changeResultSelection,
    onHighlightIndexChange: changeHighlightIndex,
    onFlush: flush,
  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(RoleBasedMessagingRecipientsInput);



// WEBPACK FOOTER //
// ./src/chat/components/RoleBasedMessagingRecipientsInput.jsx